import React, { useState, useRef, useCallback } from 'react';
import { Upload, Loader2, X, Image, Video, File } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DragDropUpload({
  onUpload,
  accept = 'image/*,video/*',
  type = 'file', // 'image', 'video', or 'file'
  currentFile = null,
  onRemove,
  className = '',
  compact = false,
  disabled = false
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [disabled]);

  const handleFile = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (type === 'image' && !file.type.startsWith('image/')) {
      return;
    }
    if (type === 'video' && !file.type.startsWith('video/')) {
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const fileUrl = `${API_URL}/api/files/${data.filename}`;
        onUpload(fileUrl);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const getAcceptTypes = () => {
    if (type === 'image') return 'image/*';
    if (type === 'video') return 'video/*';
    return accept;
  };

  const getIcon = () => {
    if (type === 'image') return Image;
    if (type === 'video') return Video;
    return File;
  };

  const Icon = getIcon();

  // Show preview if file exists
  if (currentFile && !uploading) {
    return (
      <div className={cn("relative group", className)}>
        {type === 'image' ? (
          <div className="relative">
            <img 
              src={currentFile} 
              alt="Preview" 
              className={cn(
                "rounded-lg object-cover border border-white/10",
                compact ? "w-20 h-20" : "w-full h-32"
              )}
            />
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 shadow-lg"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : type === 'video' ? (
          <div className="relative">
            <div className={cn(
              "rounded-lg bg-black/30 border border-white/10 flex items-center gap-2 p-3",
              compact ? "text-xs" : ""
            )}>
              <Video className={cn("text-cyan-400", compact ? "w-4 h-4" : "w-5 h-5")} />
              <span className="text-gray-300 truncate flex-1 text-sm">Video uploaded</span>
              {onRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="p-1 text-red-400 hover:text-red-300"
                >
                  <X className={cn(compact ? "w-3 h-3" : "w-4 h-4")} />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="relative flex items-center gap-2 p-3 bg-white/5 rounded-lg border border-white/10">
            <File className="w-5 h-5 text-cyan-400" />
            <span className="text-gray-300 truncate flex-1 text-sm">{currentFile}</span>
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="p-1 text-red-400 hover:text-red-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "relative border-2 border-dashed rounded-xl transition-all cursor-pointer",
        isDragging 
          ? "border-cyan-500 bg-cyan-500/10" 
          : "border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10",
        disabled && "opacity-50 cursor-not-allowed",
        compact ? "p-3" : "p-6",
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={getAcceptTypes()}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />
      
      <div className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "gap-1" : "gap-2"
      )}>
        {uploading ? (
          <>
            <Loader2 className={cn("animate-spin text-cyan-500", compact ? "w-5 h-5" : "w-8 h-8")} />
            <p className={cn("text-gray-400", compact ? "text-xs" : "text-sm")}>Uploading...</p>
          </>
        ) : (
          <>
            <div className={cn(
              "rounded-full bg-white/10 flex items-center justify-center",
              compact ? "w-8 h-8" : "w-12 h-12"
            )}>
              {isDragging ? (
                <Upload className={cn("text-cyan-400", compact ? "w-4 h-4" : "w-6 h-6")} />
              ) : (
                <Icon className={cn("text-gray-400", compact ? "w-4 h-4" : "w-6 h-6")} />
              )}
            </div>
            <div>
              <p className={cn("font-medium", compact ? "text-xs text-gray-300" : "text-sm text-white")}>
                {isDragging ? 'Drop to upload' : 'Drag & drop or click'}
              </p>
              {!compact && (
                <p className="text-xs text-gray-500 mt-1">
                  {type === 'image' ? 'PNG, JPG, GIF up to 10MB' : 
                   type === 'video' ? 'MP4, MOV, WebM up to 100MB' :
                   'Any file up to 100MB'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DragDropUpload;
